// 2026-05-26: localStorage cache for /traders?filter=watchlist hydration.
//
// Why: the grid uses a "placeholder → fetch → upgrade" pattern that paints
// in <100ms but causes a visible flash on every visit while /api/traders/batch
// resolves (200-500ms). For re-visits, we already know what these traders
// look like — usernames and avatars almost never change. Cache the last
// batch result locally so the second+ visit hydrates instantly with real
// names/avatars, and the network fetch only refreshes the volatile fields
// (volume, pnl, etc.) silently in the background.
//
// Cardinality: keyed by FrenFlow user_id, capped to ~50 favs typical, so
// the JSON payload stays under ~20 KB. localStorage caps at 5 MB per
// origin, so this is comfortably within budget.
//
// TTL: 7 days. Beyond that, treat the cache as stale and fall back to the
// regular placeholder flow. The fetch still runs every visit, so even
// cache hits get fresh stats — the TTL only gates whether we paint with
// cached identity (avatar/username) or with a placeholder.
//
// Privacy: the cache stores only public Polymarket trader profiles
// (address, username, avatar, public stats). No PII, no auth tokens.

import type { PolymarketTrader } from "@/lib/polymarket";

const STORAGE_KEY_PREFIX = "frenflow:watchlist-traders:v1:";
const STORAGE_KEY_PREFIX_ADDRS = "frenflow:watchlist-addresses:v1:";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface CachedWatchlist {
  cachedAt: number;
  traders: PolymarketTrader[];
}

interface CachedAddresses {
  cachedAt: number;
  addresses: string[];
}

function key(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function addrsKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX_ADDRS}${userId}`;
}

export function loadWatchlistCache(userId: string | null | undefined): PolymarketTrader[] {
  if (!userId || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CachedWatchlist;
    if (!parsed || typeof parsed.cachedAt !== "number" || !Array.isArray(parsed.traders)) {
      return [];
    }
    if (Date.now() - parsed.cachedAt > TTL_MS) return [];
    return parsed.traders;
  } catch {
    return [];
  }
}

export function saveWatchlistCache(userId: string | null | undefined, traders: PolymarketTrader[]): void {
  if (!userId || typeof window === "undefined") return;
  try {
    const payload: CachedWatchlist = { cachedAt: Date.now(), traders };
    window.localStorage.setItem(key(userId), JSON.stringify(payload));
  } catch {
    // Quota exceeded / private mode / disabled storage — silently no-op.
    // Cache is a UX optimization, not load-bearing.
  }
}

// Address list cache — small (<2 KB even at 100 favs). Persisting this
// alongside the profile cache lets re-visits of /traders?filter=watchlist
// hydrate `watchlistAddresses` synchronously before the /api/tracker/wallets
// round-trip resolves, so the placeholder "0x17e0…fe5b" rows never paint
// when both caches are warm.
export function loadWatchlistAddressesCache(userId: string | null | undefined): string[] {
  if (!userId || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(addrsKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CachedAddresses;
    if (!parsed || typeof parsed.cachedAt !== "number" || !Array.isArray(parsed.addresses)) {
      return [];
    }
    if (Date.now() - parsed.cachedAt > TTL_MS) return [];
    return parsed.addresses;
  } catch {
    return [];
  }
}

export function saveWatchlistAddressesCache(userId: string | null | undefined, addresses: string[]): void {
  if (!userId || typeof window === "undefined") return;
  try {
    const payload: CachedAddresses = { cachedAt: Date.now(), addresses };
    window.localStorage.setItem(addrsKey(userId), JSON.stringify(payload));
  } catch {
    // see saveWatchlistCache
  }
}
